import { calcPrice, getSegmentCode, isNroSegmentoPrecoCuringa, normalizeParametroPrecoBase } from '@/shared';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useCart, useClient, usePrices, useTables, useCorporateFlags, useSelectedClientScope } from '@/hooks';
import { useDbFunctions } from '../dbFunctions/useDBFunctions';
import { ProductProps, PriceProductProps, TaxSubstitutionProps, ParametroPrecoBaseProps, PercentualFornecedorProps, PrecoPromocaoClienteProps } from '@/types';

const MAX_CALCULATED_CACHE_KEYS = 1500;
const MAX_CALCULATED_CACHE_KEYS_PER_CLIENT = 600;
// Flag temporária para rollback rápido da regra global de indisponíveis.
const TEMP_HIDE_UNAVAILABLE_PRODUCTS = false;

const hasProducts = (dict?: Record<number, ProductProps>) => !!dict && Object.keys(dict).length > 0;
const getProductBrandPairs = (product: ProductProps) => {
  const brandPairs = new Set<string>();
  const packages = Array.isArray(product?.EMBALAGEM) ? product.EMBALAGEM : [];

  packages.forEach((packaging) => {
    const codLoja = Number(packaging?.COD_LOJA);
    const codSegmento = Number(packaging?.COD_SEGMENTO);
    if (Number.isFinite(codLoja) && Number.isFinite(codSegmento)) {
      brandPairs.add(`${codLoja}:${codSegmento}`);
    }
  });

  return brandPairs;
};

const mergeProductPrices = (current: ProductProps['PRECO'], incoming: ProductProps['PRECO']) => {
  const byKey = new Map<string, ProductProps['PRECO'][number]>();
  [...(current || []), ...(incoming || [])].forEach((price) => {
    if (!price) return;
    const key = [
      Number(price.COD_LOJA || 0),
      Number(price.NRO_SEGMENTO || 0),
      Number(price.NROCONDICAOPAGTO || 0),
      Number(price.QTD_EMBALAGEM || 0),
      Number(price.QTD_MULTIPLO_VENDA || 0),
      `${price.SG_EMBALAGEM || ''}`,
      `${price.TP_PROMO || ''}`,
      `${price.DT_INICIO || ''}`,
      `${price.DT_FIM || ''}`,
    ].join(':');
    byKey.set(key, price);
  });
  return Array.from(byKey.values());
};

const mergeProductPackages = (current: ProductProps['EMBALAGEM'], incoming: ProductProps['EMBALAGEM']) => {
  const byKey = new Map<string, ProductProps['EMBALAGEM'][number]>();
  [...(current || []), ...(incoming || [])].forEach((pack) => {
    if (!pack) return;
    const key = [
      Number(pack.COD_LOJA || 0),
      Number(pack.COD_SEGMENTO || 0),
      Number(pack.QTD_EMBALAGEM || 0),
      Number(pack.QTD_MULTIPLO_VENDA || 0),
      `${pack.SG_EMBALAGEM || ''}`,
    ].join(':');
    byKey.set(key, pack);
  });
  return Array.from(byKey.values());
};

const mergeProductsByCode = (current: ProductProps | undefined, incoming: ProductProps): ProductProps => {
  if (!current) return incoming;

  return {
    ...current,
    ...incoming,
    PRECO: mergeProductPrices(current.PRECO, incoming.PRECO),
    EMBALAGEM: mergeProductPackages(current.EMBALAGEM, incoming.EMBALAGEM),
    ATRIBUTOS: (incoming.ATRIBUTOS?.length ? incoming.ATRIBUTOS : current.ATRIBUTOS) || [],
    IMAGEM: incoming.IMAGEM || current.IMAGEM || '',
    ultimaCompra: incoming.ultimaCompra || current.ultimaCompra || {},
  };
};

const buildProductDict = (products: ProductProps[]) => {
  return products.reduce((acc, product) => {
    acc[product.COD_PRODUTO] = mergeProductsByCode(acc[product.COD_PRODUTO], product);
    return acc;
  }, {} as Record<number, ProductProps>);
};

export const useCalcPrice = () => {
  const { tables, setOnTables } = useTables();
  const { getOnDB } = useDbFunctions();
  const { getLimit } = useCart();

  const getDictProducts = useCallback(() => {
    const cachedDict = tables.current.dicionario_produtos as Record<number, ProductProps> | undefined;
    const produtos = Array.isArray(tables.current.produtos) ? tables.current.produtos : [];
    if (!produtos.length) {
      return cachedDict && Object.keys(cachedDict).length > 0 ? cachedDict : ({} as Record<number, ProductProps>);
    }

    const uniqueCodes = new Set(produtos.map((produto) => produto.COD_PRODUTO));
    const hasDuplicateCodes = uniqueCodes.size < produtos.length;

    if (!hasDuplicateCodes && cachedDict && Object.keys(cachedDict).length > 0) {
      return cachedDict;
    }

    const dicionario = buildProductDict(produtos);
    const cachedKeys = cachedDict ? Object.keys(cachedDict).length : 0;
    const nextKeys = Object.keys(dicionario).length;

    const shouldPersistMergedDict = hasDuplicateCodes || !cachedDict || cachedKeys !== nextKeys;
    if (shouldPersistMergedDict) {
      setOnTables(dicionario, 'dicionario_produtos');
    }

    return dicionario;
  }, [tables, setOnTables]);

  const { selectedBrandPair: selectedBrand, brandTheme, brands } = useCorporateFlags();

  const { selectedClient } = useClient();

  const baseSelectedClientScope = useSelectedClientScope();

  const { catalog: hookCatalog, setProducts, isLoaded, isLoading: isLoadingCatalog, calculatedList } = usePrices();
  const recoveryAttemptsByClientRef = useRef<Record<number, number>>({});
  const recoveryInFlightByClientRef = useRef<Record<number, Promise<Record<number, ProductProps>>>>({});
  const previousBrandKeyRef = useRef<string>('');

  const ensureDictProducts = useCallback(async (clientCode?: number) => {
    const currentDict = getDictProducts();
    if (hasProducts(currentDict)) return currentDict;
    if (!clientCode) return {} as Record<number, ProductProps>;

    const inFlight = recoveryInFlightByClientRef.current[clientCode];
    if (inFlight) return inFlight;

    const recover = async () => {
      // Evita custo recorrente em dispositivos críticos, mas permite retentativas em corrida de hidratação.
      const attempts = recoveryAttemptsByClientRef.current[clientCode] || 0;
      if (attempts >= 3) {
        return {} as Record<number, ProductProps>;
      }
      recoveryAttemptsByClientRef.current[clientCode] = attempts + 1;

      const fromDbDict = await getOnDB('dicionario_produtos', clientCode, 0, true);
      if (fromDbDict && !Array.isArray(fromDbDict) && hasProducts(fromDbDict as Record<number, ProductProps>)) {
        setOnTables(fromDbDict as Record<number, ProductProps>, 'dicionario_produtos', true);
        return fromDbDict as Record<number, ProductProps>;
      }

      const fromDbProducts = await getOnDB('produtos', clientCode, 0, true);
      if (!Array.isArray(fromDbProducts) || !fromDbProducts.length) return {} as Record<number, ProductProps>;

      const rebuiltDict = buildProductDict(fromDbProducts as ProductProps[]);

      setOnTables(rebuiltDict, 'dicionario_produtos', true);
      return rebuiltDict;
    };

    const recoveryPromise = recover()
      .catch(() => ({} as Record<number, ProductProps>))
      .finally(() => {
        delete recoveryInFlightByClientRef.current[clientCode];
      });

    recoveryInFlightByClientRef.current[clientCode] = recoveryPromise;
    return recoveryPromise;
  }, [getDictProducts, getOnDB, setOnTables]);

  useEffect(() => {
    const cod = selectedClient?.COD_CLIENTE;
    if (!cod) return;

    // Novo cliente selecionado: zera contador de recuperação.
    if (recoveryAttemptsByClientRef.current[cod] == null) {
      recoveryAttemptsByClientRef.current[cod] = 0;
    }
  }, [selectedClient?.COD_CLIENTE]);

  const pruneCalculatedListCache = useCallback((clientCode: number) => {
    const clientPrefix = `${clientCode}-`;
    const keys = Object.keys(calculatedList.current);
    const clientKeys = keys.filter((key) => key.startsWith(clientPrefix));

    if (clientKeys.length > MAX_CALCULATED_CACHE_KEYS_PER_CLIENT) {
      const extraClientKeys = clientKeys.length - MAX_CALCULATED_CACHE_KEYS_PER_CLIENT;
      for (let i = 0; i < extraClientKeys; i++) {
        delete calculatedList.current[clientKeys[i]];
      }
    }

    const allKeys = Object.keys(calculatedList.current);
    if (allKeys.length > MAX_CALCULATED_CACHE_KEYS) {
      const extraGlobalKeys = allKeys.length - MAX_CALCULATED_CACHE_KEYS;
      for (let i = 0; i < extraGlobalKeys; i++) {
        delete calculatedList.current[allKeys[i]];
      }
    }
  }, [calculatedList]);

  const substTrib = tables.current.cod_regime_tributario;
  const selectedLojaFromBrand = useMemo(() => {
    const lojas = Array.isArray(selectedClient?.lojas) ? selectedClient.lojas : [];
    if (!selectedBrand?.codLoja || !selectedBrand?.codSegmento || !lojas.length) return null;

    const codLoja = Number(selectedBrand.codLoja);
    const codSegmento = Number(selectedBrand.codSegmento);
    if (!Number.isFinite(codLoja) || !Number.isFinite(codSegmento)) return null;

    return lojas.find((loja: any) => {
      if (Number(loja?.COD_LOJA) !== codLoja) return false;
      const segmentosLoja = Array.isArray(loja?.COD_SEGMENTO) ? loja.COD_SEGMENTO : [];
      return segmentosLoja.some((segmento: any) => Number(getSegmentCode(segmento)) === codSegmento);
    }) || null;
  }, [selectedClient?.lojas, selectedBrand?.codLoja, selectedBrand?.codSegmento]);

  const selectedClientScope = useMemo(() => {
    if (!baseSelectedClientScope?.COD_CLIENTE) return baseSelectedClientScope;
    if (!selectedLojaFromBrand) return baseSelectedClientScope;

    const codSegmentoFromBrand = Number(selectedBrand?.codSegmento);
    const segmentosLoja = Array.isArray(selectedLojaFromBrand.COD_SEGMENTO) ? selectedLojaFromBrand.COD_SEGMENTO : [];
    const segmentoAtivo = segmentosLoja.find((segmento: any) => Number(getSegmentCode(segmento)) === codSegmentoFromBrand);

    const payLoja = selectedLojaFromBrand.COD_PAGAMENTO;
    const payBase = baseSelectedClientScope.COD_PAGAMENTO;
    const COD_PAGAMENTO =
      Array.isArray(payLoja) && payLoja.length > 0
        ? payLoja
        : Array.isArray(payBase) && payBase.length > 0
          ? payBase
          : Array.isArray(payLoja)
            ? payLoja
            : Array.isArray(payBase)
              ? payBase
              : [];

    return {
      ...baseSelectedClientScope,
      ...selectedLojaFromBrand,
      COD_SEGMENTO: segmentoAtivo ? [segmentoAtivo] : segmentosLoja,
      COD_PAGAMENTO,
    };
  }, [baseSelectedClientScope, selectedLojaFromBrand, selectedBrand?.codSegmento]);

  const segmentos = useMemo(() => selectedClientScope?.COD_SEGMENTO?.map((c: any) => getSegmentCode(c)) || [], [selectedClientScope]);
  const condicoes = useMemo(
    () =>
      (selectedClientScope?.COD_PAGAMENTO || [])
        .map((i: any) => Number(i?.cod_condicao ?? i?.COD_CONDICAO))
        .filter((n: number) => Number.isFinite(n) && n > 0),
    [selectedClientScope]
  );

  const activeBrandKey = useMemo(
    () => `${selectedClientScope?.COD_LOJA || ''}:${segmentos?.[0] || ''}`,
    [selectedClientScope?.COD_LOJA, segmentos]
  );
  /** Atualizado a cada render — `getCatalog` compara snapshot após `await` para descartar resultado obsoleto. */
  const catalogScopeKeyRef = useRef('');
  catalogScopeKeyRef.current = `${selectedClientScope?.COD_CLIENTE ?? ''}:${activeBrandKey}`;
  const isFortBrand = useMemo(() => {
    return (brandTheme?.name || '').toLowerCase().includes('fort');
  }, [brandTheme?.name]);
  const fortBrandPairs = useMemo(() => {
    const pairs = new Set<string>();
    brands.forEach((brandItem: any) => {
      if (!(brandItem?.name || '').toLowerCase().includes('fort')) return;
      const rules = Array.isArray(brandItem?.cod_loja) ? brandItem.cod_loja : [];
      rules.forEach((rule: any) => {
        const codLoja = Number(rule?.cod_loja);
        const codSegmento = Number(rule?.cod_segmento);
        if (Number.isFinite(codLoja) && Number.isFinite(codSegmento)) {
          pairs.add(`${codLoja}:${codSegmento}`);
        }
      });
    });
    return pairs;
  }, [brands]);
  const nonFortBrandPairs = useMemo(() => {
    const pairs = new Set<string>();
    brands.forEach((brandItem: any) => {
      if ((brandItem?.name || '').toLowerCase().includes('fort')) return;
      const rules = Array.isArray(brandItem?.cod_loja) ? brandItem.cod_loja : [];
      rules.forEach((rule: any) => {
        const codLoja = Number(rule?.cod_loja);
        const codSegmento = Number(rule?.cod_segmento);
        if (Number.isFinite(codLoja) && Number.isFinite(codSegmento)) {
          pairs.add(`${codLoja}:${codSegmento}`);
        }
      });
    });
    return pairs;
  }, [brands]);
  const catalog = useMemo(() => {
    const cod = selectedClient?.COD_CLIENTE as number | undefined;
    if (cod == null) return [];
    return hookCatalog[cod]?.[activeBrandKey] ?? [];
  }, [hookCatalog, selectedClient?.COD_CLIENTE, activeBrandKey]);

  /** Evita dependência de `catalog` em `getCatalog`/`loadStates` (atualizações do catálogo recriariam o callback e re-disparariam efeitos). */
  const catalogForActiveScopeLenRef = useRef(0);
  catalogForActiveScopeLenRef.current = catalog.length;

  const handlePrices = useCallback(
    (
      condicoes: number[],
      product: ProductProps,
      parametroPrecoBase: ParametroPrecoBaseProps[],
      percentualFornecedor: PercentualFornecedorProps[],
      listSubstTrib: TaxSubstitutionProps[],
      precoPromocao: PrecoPromocaoClienteProps,
      from: string
    ): PriceProductProps[] => {
      if (!selectedClientScope?.COD_CLIENTE || !product?.COD_PRODUTO) return [] as PriceProductProps[];

      // PRECO pode vir como mapa (legado) ou como array normalizado.
      const rawPrecos: any = product?.PRECO || {};
      const precoList: any[] = Array.isArray(rawPrecos) ? rawPrecos : Object.values(rawPrecos);
      // Guard antigo `isLoaded.current[catalogLoadedKey]` retornava `[]` para qualquer produto
      // de um escopo previamente marcado como vazio. Isso quebrava o carrinho/checkout, que
      // mostram itens cujo escopo (loja+segmento) pode ser diferente da bandeira ativa atual.
      // Listas (Products/Category/SearchPage/FilterBar) iteram sobre o `catalog` filtrado, que
      // já é vazio nesses casos — então remover o guard não as afeta.
      // A nova checagem é baseada no próprio dado do produto: sem PRECO raw, sem cálculo.
      if (!precoList.length) return [] as PriceProductProps[];

      const precos = [] as PriceProductProps[];
      const baseList = Array.isArray(parametroPrecoBase) ? parametroPrecoBase : [];
      const tribList = Array.isArray(listSubstTrib) ? listSubstTrib : [];

      const precoPromocaoByClient = precoPromocao?.[selectedClientScope.COD_CLIENTE] || {};

      // Indexa rapidamente para evitar .find() dentro do loop principal.
      const parametroPrecoBaseByCondFaixa = baseList.reduce(
        (acc, p) => {
          const condMap = acc.get(p.NROCONDICAOPAGTO) || new Map<string, ParametroPrecoBaseProps>();
          condMap.set(p.FAIXAACRFINANCEIRO, p);
          acc.set(p.NROCONDICAOPAGTO, condMap);
          return acc;
        },
        new Map<number, Map<string, ParametroPrecoBaseProps>>()
      );

      const percentualFornecedorBySeq = new Map<number, number>(
        (percentualFornecedor || []).map((f) => [f.SEQFAMILIA, f.PERACRESCDESC] as const)
      );

      const precosFiltrados = precoList.filter((p: any) => {
        const lojaCliente = selectedClientScope?.COD_LOJA;
        const codLojaOk =
          p?.COD_LOJA == null ||
          (lojaCliente != null && Number(p.COD_LOJA) === Number(lojaCliente));

        const seg = p?.NRO_SEGMENTO;
        // Alinha com Categories (sem segmentos no cliente = não filtra por segmento) e com curingas ERP (998/999).
        const segmentoOk =
          seg == null ||
          isNroSegmentoPrecoCuringa(seg) ||
          segmentos.length === 0 ||
          segmentos.some((s) => Number(s) === Number(seg));

        return codLojaOk && segmentoOk;
      });

      const safeCondicoes = Array.isArray(condicoes) ? condicoes : [];
      for (const cond of safeCondicoes) {
        for (const precoJson of precosFiltrados) {
          // Embalagem base (geralmente UN) correspondente à QTD_EMBALAGEM do preço
          const embalagemBase = (product.EMBALAGEM || []).find((e) => e.QTD_EMBALAGEM === precoJson.QTD_EMBALAGEM);
          // Apenas adiciona fallback para múltiplos de venda (PK) quando houver
          const embalagensPK = (product.EMBALAGEM || []).filter(
            (e) => e.SG_EMBALAGEM === 'PK' && (e.QTD_MULTIPLO_VENDA || 0) > 1 && e.QTD_EMBALAGEM === precoJson.QTD_EMBALAGEM
          );
          const embalagens = [embalagemBase, ...embalagensPK].filter(Boolean) as any[];

          // Preço promocional por quantidade de embalagem (base unitária)
          const precoPromo =
            precoPromocaoByClient?.[precoJson.QTD_EMBALAGEM]?.[precoJson.COD_PRODUTO]?.[selectedClientScope.COD_LOJA]?.[
              precoJson.NRO_SEGMENTO
            ];
          const priceValidation: any = precoPromo?.VLR_PRECO ? precoPromo : precoJson;

          const percPreco = parametroPrecoBaseByCondFaixa.get(cond)?.get(priceValidation.FAIXAACRFINANCEIRO);

          if (!percPreco) {
            continue;
          }

          // Itera por TODAS as embalagens compatíveis, gerando preço também para múltiplos de venda (PK)
          for (const embalagem of embalagens) {
            const percentualForcedor = percentualFornecedorBySeq.get(precoJson.SEQFAMILIA) || 0;

            const isPrecoPromo =
              precoPromo && precoPromo.QTD_EMBALAGEM === embalagem?.QTD_EMBALAGEM && precoPromo.VLR_PRECO < precoJson.VLR_PRECO;

            const basePriceJson = isPrecoPromo ? precoPromo : precoJson;

            const precoIn: PriceProductProps = {
              ...precoJson,
              ...(isPrecoPromo && { COD_CLIENTE: (precoPromo as any).COD_CLIENTE }),
              COD_LOJA: selectedClientScope.COD_LOJA,
              COD_PRODUTO: precoJson.COD_PRODUTO,
              COD_TRIBUTACAO_PRODUTO: basePriceJson.COD_TRIBUTACAO_PRODUTO,
              DESTACA_ST: basePriceJson.DESTACA_ST,
              DT_FIM: '',
              DT_INICIO: '',
              FAIXAACRFINANCEIRO: basePriceJson.FAIXAACRFINANCEIRO,
              INDUSAACRESCTABVENDA: basePriceJson.INDUSAACRESCTABVENDA,
              NROCONDICAOPAGTO: cond,
              NRO_SEGMENTO: basePriceJson.NRO_SEGMENTO,
              // Usa a embalagem corrente: para PK garante QTD_MULTIPLO_VENDA correta
              QTD_EMBALAGEM: embalagem.QTD_EMBALAGEM,
              QTD_MULTIPLO_VENDA: embalagem.QTD_MULTIPLO_VENDA,
              SEQFAMILIA: basePriceJson.SEQFAMILIA,
              NROTABVENDA: `${selectedClientScope.COD_TABELA_VENDA}`,
              PERACRESCDESC: percentualForcedor,
              PERACRENTREGA: percPreco.PERACRENTREGA,
              PERACRFINANCEIRO: percPreco.PERACRFINANCEIRO,
              SG_EMBALAGEM: embalagem.SG_EMBALAGEM,
              TP_PROMO: basePriceJson.TP_PROMO,
              // Valor base por unidade; calcPrice cuidará de multiplicar por QTD_MULTIPLO_VENDAsrc/talons/calcPrices/useCalcPrice.ts
              VLR_PRECO: basePriceJson.VLR_PRECO,
              // Pauta em nível unitário; calcPrice/ST cuidará da multiplicação/uso correto
              VLR_PAUTA: precoJson.VLR_PAUTA,
            } as PriceProductProps;

            precos.push(precoIn);
          }
        }
      }

      const calculatedPrices = calcPrice(selectedClientScope, precos, tribList);

      return calculatedPrices;
    },
    [selectedClientScope, segmentos]
  );

  const loadStates = useCallback(
    async (
      catalogList: ProductProps[],
      ordered = true,
      filterStock = false,
      forceLoad = false,
      scopeKeyForCatalog = activeBrandKey
    ) => {
      if (catalogForActiveScopeLenRef.current && !forceLoad) return catalogList;

      const list = [] as ProductProps[];
      const shouldHideUnavailableProducts = TEMP_HIDE_UNAVAILABLE_PRODUCTS || filterStock;
      for (const product of catalogList) {
        if (shouldHideUnavailableProducts) {
          const limit = getLimit(product.COD_PRODUTO);
          const emb = product?.EMBALAGEM || [];
          const hasEmbLimit = emb.some((item) => limit >= item.QTD_EMBALAGEM);
          if (limit <= 0 || !emb.length || !hasEmbLimit) continue;
        }
        list.push(product);
      }

      if (ordered) {
        list.sort((a, b) => {
          if (a.DESC_PRODUTO < b.DESC_PRODUTO) return -1;
          if (a.DESC_PRODUTO > b.DESC_PRODUTO) return 1;
          return 0;
        });
      }

      if (!selectedClientScope?.COD_CLIENTE) return list;
      setProducts(selectedClientScope.COD_CLIENTE, scopeKeyForCatalog, list);

      return list;
    },
    [getLimit, selectedClientScope, activeBrandKey, setProducts]
  );

  const getProduct = useCallback(
    (id: number) => {
      return catalog?.find((item) => item?.COD_PRODUTO === id);
    },
    [catalog]
  );

  const getCatalog = useCallback(
    async (ordered = true, timer?: number, filterStock = false, forceLoad?: boolean) => {

      const codClient = selectedClientScope?.COD_CLIENTE;
      if (!codClient) return;
      const catalogLoadKey = `${codClient}:${activeBrandKey}`;
      if (!forceLoad && isLoaded.current[catalogLoadKey]) {
        return isLoaded.current[catalogLoadKey];
      }
      if (isLoadingCatalog.current) {
        if (!forceLoad) {
          return isLoaded.current[catalogLoadKey];
        }
        await new Promise((resolve) => setTimeout(resolve, 120));
        return getCatalog(ordered, timer, filterStock, forceLoad);
      }
      
      if (catalogForActiveScopeLenRef.current && !forceLoad) return;

      // Escopo no momento em que esta carga começou (evita "flash" de catálogo de outra loja
      // quando duas trocas de brand disparam `getCatalog` e a requisição antiga termina por último).
      const snapshotAtStart = `${selectedClientScope?.COD_CLIENTE ?? ''}:${activeBrandKey}`;
      const scopeKeySnapshot = activeBrandKey;

      isLoadingCatalog.current = true;
      try {
        const dictProducts = await ensureDictProducts(codClient);
        if (snapshotAtStart !== catalogScopeKeyRef.current) return;
        if (!hasProducts(dictProducts)) return;

        const segmentosMap = segmentos.reduce((acc, s) => {
          acc[s] = true;
          return acc;
        }, {} as Record<number, boolean>);

        const loadedCatalog = Object.values(dictProducts).reduce((acc, p: ProductProps) => {
          const productPairs = getProductBrandPairs(p);
          if (isFortBrand && fortBrandPairs.size > 0) {
            const hasFortPair = [...productPairs].some((pair) => fortBrandPairs.has(pair));
            if (!hasFortPair) {
              return acc;
            }
          }

          const codLojaAtiva = Number(selectedClientScope?.COD_LOJA);

          const hasLojaFilter = Number.isFinite(codLojaAtiva) && codLojaAtiva > 0;
          const hasSegmentFilter = Object.keys(segmentosMap).length > 0;
          const embalagem = (p.EMBALAGEM || []).filter((e) => {
            const lojaOk = !hasLojaFilter || Number(e?.COD_LOJA) === codLojaAtiva;
            const segmentoOk = !hasSegmentFilter || !!segmentosMap[Number(e?.COD_SEGMENTO)];
            return lojaOk && segmentoOk;
          });

          if (!embalagem?.length) return acc;

          acc.push({
            ...p,
            EMBALAGEM: embalagem,
          });

          return acc;
        }, [] as ProductProps[]);

        if (snapshotAtStart !== catalogScopeKeyRef.current) return;

        const list = await loadStates(loadedCatalog, ordered, filterStock, forceLoad, scopeKeySnapshot);
        if (snapshotAtStart !== catalogScopeKeyRef.current) return;

        await new Promise((resolve) => setTimeout(() => resolve(true), timer || 1200));

        if (snapshotAtStart !== catalogScopeKeyRef.current) return;

        if (!list.length && loadedCatalog.length === 0) {
          setProducts(codClient, scopeKeySnapshot, list);
          isLoaded.current[catalogLoadKey] = true;
        } else if (loadedCatalog.length > 0) {
          // Garante "negative cache" simétrica: se um sync subsequente trouxer produtos para um
          // escopo previamente marcado como vazio, libera o flag para recálculos passarem por
          // `getCatalog` normalmente.
          isLoaded.current[catalogLoadKey] = false;
        }
        return list;
      } finally {
        isLoadingCatalog.current = false;
      }
    },
    [
      selectedClientScope,
      activeBrandKey,
      loadStates,
      ensureDictProducts,
      isLoaded,
      isLoadingCatalog,
      segmentos,
      isFortBrand,
      fortBrandPairs,
      nonFortBrandPairs,
      setProducts,
    ]
  );

  const reloadCatalog = useCallback(async (ordered?: boolean, timer?: number, filterStock = false) => {
    const codClient = selectedClientScope?.COD_CLIENTE;
    if (!codClient || !activeBrandKey) return;

    const dictProducts = await ensureDictProducts(codClient);
    if (!hasProducts(dictProducts)) return;
    const reloadKey = `${codClient}:${activeBrandKey}`;
    setProducts(codClient, activeBrandKey, []);
    delete isLoaded.current[reloadKey];
    await getCatalog(ordered, timer, filterStock, true);
  }, [ensureDictProducts, selectedClientScope?.COD_CLIENTE, activeBrandKey, setProducts, getCatalog]);

  const getCatalogRef = useRef(getCatalog);
  useEffect(() => {
    getCatalogRef.current = getCatalog;
  }, [getCatalog]);

  useEffect(() => {
    const cod = selectedClient?.COD_CLIENTE;
    if (!cod || !activeBrandKey) return;
    // Evita rerun por identidade do callback durante hidratação/derivações de contexto.
    getCatalogRef.current(undefined, undefined, false, true);
  }, [selectedClient?.COD_CLIENTE, activeBrandKey]);

  useEffect(() => {
    previousBrandKeyRef.current = '';
  }, [selectedClient?.COD_CLIENTE]);

  useEffect(() => {
    const codClient = selectedClientScope?.COD_CLIENTE;
    if (!codClient || !activeBrandKey) return;
    if (!previousBrandKeyRef.current) {
      previousBrandKeyRef.current = activeBrandKey;
      return;
    }
    if (previousBrandKeyRef.current === activeBrandKey) return;

    const outgoingBrandKey = previousBrandKeyRef.current;
    previousBrandKeyRef.current = activeBrandKey;

    const oldPricePrefix = `${codClient}-${outgoingBrandKey}-`;
    Object.keys(calculatedList.current).forEach((key) => {
      if (key.startsWith(oldPricePrefix)) {
        delete calculatedList.current[key];
      }
    });
  }, [activeBrandKey, selectedClientScope?.COD_CLIENTE, calculatedList]);

  const findPrice = (price: PriceProductProps[], option_uid: string, paymentCondition: number) => {
    if (!price || !Array.isArray(price) || !price.length) return {} as PriceProductProps;
    const [packing, quantity] = option_uid?.split('_') || [];
    const correctPrice = price
      .filter((p) => p.COD_LOJA === selectedClientScope?.COD_LOJA)
      ?.find((p) => {
        // Para múltiplos de venda (ex.: PK), procurar pela própria embalagem e pelo QTD_MULTIPLO_VENDA
        if (packing === 'PK' || (p?.QTD_MULTIPLO_VENDA || 0) > 1) {
          return (
            p.SG_EMBALAGEM === packing && p.QTD_MULTIPLO_VENDA?.toString() === quantity && p.NROCONDICAOPAGTO === paymentCondition
          );
        }
        // Caso padrão (UN ou outras embalagens sem múltiplo)
        return p.SG_EMBALAGEM === packing && p.QTD_EMBALAGEM?.toString() === quantity && p.NROCONDICAOPAGTO === paymentCondition;
      });
    return correctPrice || ({} as PriceProductProps);
  };

  const getPrices = useCallback(
    (product: ProductProps): PriceProductProps[] => {
      if (!selectedClientScope?.COD_CLIENTE || !product?.COD_PRODUTO) return [] as PriceProductProps[];

      const keyCalculated = `${selectedClientScope.COD_CLIENTE}-${activeBrandKey}-${product.COD_PRODUTO}`;
      if (calculatedList.current[keyCalculated]) {
        const cached = calculatedList.current[keyCalculated];
        // Refresca a ordem de inserção para priorizar itens usados recentemente.
        delete calculatedList.current[keyCalculated];
        calculatedList.current[keyCalculated] = cached;
        return cached;
      }

      const codLoja = selectedClientScope.COD_LOJA;
      const tabVenda = selectedClientScope.COD_TABELA_VENDA;
      const percentual = (tables.current.percentual_fornecedor as any) ?? [];
      const listSubstTrib = (tables.current.cod_regime_tributario as any) ?? [];
      const parametroPrecoPromocao = tables.current.precoPromocaoCliente;
      const percentualFornecedor = (Array.isArray(percentual) ? percentual : []).filter(
        (p: PercentualFornecedorProps) => `${p.NROTABVENDA}` === `${tabVenda}`
      );

      const parametroPreco = normalizeParametroPrecoBase(
        tables.current.parametro_preco_base,
        codLoja,
        tabVenda,
        condicoes
      );

      const precos = handlePrices(
        condicoes,
        product,
        parametroPreco,
        percentualFornecedor,
        listSubstTrib,
        parametroPrecoPromocao,
        'useCalcPrice'
      );
      
      if (precos.length > 0) {
        calculatedList.current[keyCalculated] = precos;
        pruneCalculatedListCache(selectedClientScope.COD_CLIENTE);
      }
      return precos;
    },
    [selectedClientScope, pruneCalculatedListCache, condicoes, tables, handlePrices, activeBrandKey]
  );

  return {
    catalog,
    substTrib,
    isLoadingCatalog,
    isLoaded: isLoaded.current,
    getProduct,
    getCatalog,
    reloadCatalog,
    handlePrices,
    findPrice,
    getPrices,
  };
};
